import React, { useState } from 'react';
import { Box, Modal, Typography, TextField, Divider, Link, Alert } from '@mui/material';
import ActionButton from '../components/ActionButton.jsx';
import { PALETTE, BORDERS, alpha } from '../theme';
import { useAuth, friendlyAuthError } from './AuthContext';

/**
 * One global sign-in / sign-up modal, controlled entirely by AuthContext
 * (`dialogOpen`). Opened either by the account menu ("Log in") or by any gated
 * action via `requireAccount()` (e.g. the Save button). On success the auth
 * listener in AuthContext closes it — this component only collects input,
 * shows errors, and manages its own submitting state.
 */

// "Continue with Google" — white pill with the multi-color G mark, matching
// Google's brand guidance so it reads as the trusted one-tap option.
const GoogleMark = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
);

const AuthDialog = () => {
    const { dialogOpen, closeAuthDialog, loginEmail, signupEmail, loginGoogle, resetPassword } = useAuth();

    const [mode, setMode] = useState('login'); // 'login' | 'signup'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);

    const isSignup = mode === 'signup';

    const reset = () => {
        setError(null);
        setNotice(null);
        setPassword('');
    };

    const handleClose = () => {
        reset();
        setEmail('');
        setMode('login');
        closeAuthDialog();
    };

    const run = async (fn) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await fn();
            // Success closes the dialog via the auth listener; nothing to do here.
        } catch (err) {
            setError(friendlyAuthError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (!email.trim() || !password) return;
        run(() => (isSignup ? signupEmail(email, password) : loginEmail(email, password)));
    };

    const handleForgot = async () => {
        if (!email.trim()) {
            setError('Enter your email above first, then tap “Forgot password”.');
            return;
        }
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await resetPassword(email);
            setNotice(`Password reset link sent to ${email.trim()}.`);
        } catch (err) {
            setError(friendlyAuthError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={dialogOpen} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 420,
                    maxWidth: '92vw',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(10px)',
                    border: BORDERS.soft,
                    borderRadius: 3,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    p: { xs: 3, sm: 4 },
                }}
            >
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, textAlign: 'center', color: PALETTE.text }}>
                    {isSignup ? 'Create your account' : 'Log in'}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: PALETTE.textMuted, mt: 0.5, mb: 2.5 }}>
                    Save your maps and reopen them anytime.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

                {/* Google — the trusted one-tap path, given top billing. */}
                <Box
                    component="button"
                    type="button"
                    onClick={() => run(loginGoogle)}
                    disabled={busy}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.25,
                        width: '100%',
                        height: 48,
                        borderRadius: 2,
                        border: BORDERS.default,
                        backgroundColor: PALETTE.surface,
                        color: PALETTE.textBody,
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        cursor: busy ? 'default' : 'pointer',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': { backgroundColor: alpha('text', 0.03), borderColor: alpha('text', 0.18) },
                        '&:disabled': { opacity: 0.6, cursor: 'default' },
                    }}
                >
                    <GoogleMark />
                    Continue with Google
                </Box>

                <Divider sx={{ my: 2.5, color: PALETTE.textFaint, fontSize: '0.8rem' }}>or</Divider>

                <form onSubmit={handleEmailSubmit}>
                    <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        autoComplete="email"
                        disabled={busy}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        autoComplete={isSignup ? 'new-password' : 'current-password'}
                        disabled={busy}
                        helperText={isSignup ? 'At least 6 characters.' : ' '}
                    />

                    <ActionButton
                        variant="primary"
                        onClick={handleEmailSubmit}
                        loading={busy}
                        disabled={!email.trim() || !password}
                        sx={{ width: '100%', mt: 1 }}
                    >
                        {isSignup ? 'Sign up' : 'Log in'}
                    </ActionButton>
                </form>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => {
                            reset();
                            setMode(isSignup ? 'login' : 'signup');
                        }}
                        sx={{ color: PALETTE.primary, fontWeight: 600, fontSize: '0.85rem' }}
                    >
                        {isSignup ? 'Already have an account? Log in' : 'New here? Create an account'}
                    </Link>
                    {!isSignup && (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={handleForgot}
                            sx={{ color: PALETTE.textMuted, fontSize: '0.85rem' }}
                        >
                            Forgot password?
                        </Link>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default AuthDialog;
